import React from 'react';
import { OrgIntegrationService, UserIntegrationService } from 'sb-lib/types';
import { IntegrationData, OrgWithPlan } from 'types';
import AcuityIcon from 'shared/assets/images/acuity-powerup.webp';
import ApolloIcon from 'shared/assets/images/apollo.png';
import CalendlyIcon from 'shared/assets/images/calendly-powerup.png';
import ChiliPiperIcon from 'shared/assets/images/chili-piper-powerup.png';
import ClearbitIcon from 'shared/assets/images/clearbit-powerup.png';
import GongIcon from 'shared/assets/images/gong-powerup.png';
import GoogleCalIcon from 'shared/assets/images/google-calendar.png';
import OutlookIcon from 'shared/assets/images/outlook.png';
import HubspotIcon from 'shared/assets/images/hubspot-powerup.png';
import IntercomIcon from 'shared/assets/images/intercom-powerup.png';
import OutreachIcon from 'shared/assets/images/outreach.png';
import SalesforceIcon from 'shared/assets/images/salesforce-powerup.png';
import SalesloftIcon from 'shared/assets/images/salesloft-powerup.png';
import SixsenseIcon from 'shared/assets/images/sixsense-powerup.png';
import SlackIcon from 'shared/assets/images/slack-powerup.png';
import TeamsIcon from 'shared/assets/images/microsoft-teams.png';
import ZapierIcon from 'shared/assets/images/zapier-powerup.png';
import ZendeskIcon from 'shared/assets/images/zendesk-powerup.png';
import ZoomIcon from 'shared/assets/images/zoom.png';

export const orgIntegrationServices: OrgIntegrationService[] = [
  OrgIntegrationService.acuity,
  OrgIntegrationService.apollo,
  OrgIntegrationService.calendly,
  OrgIntegrationService.chilipiper,
  OrgIntegrationService.clearbit,
  OrgIntegrationService.gong,
  OrgIntegrationService.hubspot,
  OrgIntegrationService.intercom,
  OrgIntegrationService.outreach,
  OrgIntegrationService.salesforce,
  OrgIntegrationService.salesloft,
  OrgIntegrationService.slack,
  OrgIntegrationService.teams,
  OrgIntegrationService.zendesk,
];

export const userIntegrationServices: UserIntegrationService[] = [
  UserIntegrationService['google-calendar'],
  UserIntegrationService['microsoft-teams'],
  UserIntegrationService['outlook-calendar'],
  UserIntegrationService.zoom,
];

const makeDocsLink = (service: string) =>
  `https://docs.servicebell.com/integrations/${service}`;

const makeRedirectUri = (service: string, isUser?: boolean) => {
  const endpoint = isUser ? 'user-oauth' : 'oauth';
  return `${process.env.PUBLIC_HOST_URL}/${endpoint}/callback/${service}`;
};

export const orgIntegrationData: Record<OrgIntegrationService, IntegrationData> = {
  [OrgIntegrationService.gong]: {
    name: 'Gong',
    description:
      'Gong captures customer interactions then delivers insights at scale, empowering teams to make decisions based on data instead of opinions.',
    icon: GongIcon,
    docs: makeDocsLink('gong'),
    configured: !!process.env.GONG_CLIENT_ID,
    authUrl: () =>
      `https://app.gong.io/oauth2/authorize` +
      `?client_id=${process.env.GONG_CLIENT_ID}` +
      `&scopes=${encodeURIComponent(
        ['api:calls:create', 'api:calls:read:basic', 'api:users:read'].join(' '),
      )}` +
      `&redirect_uri=${makeRedirectUri('gong')}`,
  },
  [OrgIntegrationService.clearbit]: {
    name: 'Clearbit',
    description:
      'Real-time intelligence across your stack to improve acquisition, conversion, & operations',
    icon: ClearbitIcon,
    docs: makeDocsLink('clearbit'),
    configured: true,
    authUrl: () => 'sup',
  },
  [OrgIntegrationService.sixsense]: {
    name: '6sense',
    description:
      '6sense arms your revenue team with the data and visibility it needs to create and convert high-quality pipeline into revenue',
    icon: SixsenseIcon,
    configured: true,
    authUrl: () => 'sup',
    planCapKey: 'baseSixsenseCompanyDetailFetches',
    planDataKey: 'sixsenseData',
  },
  [OrgIntegrationService.intercom]: {
    name: 'Intercom',
    description:
      'Convert more website visitors into loyal customers with personalized messages, bots & chat',
    icon: IntercomIcon,
    docs: makeDocsLink('intercom'),
    configured: !!process.env.INTERCOM_CLIENT_ID,
    authUrl: () =>
      `https://app.intercom.com/oauth?client_id=${
        process.env.INTERCOM_CLIENT_ID
      }&redirect_uri=${makeRedirectUri('intercom')}&state=${generateOAuthStateValue()}`,
  },
  [OrgIntegrationService.calendly]: {
    name: 'Calendly',
    description: 'Schedule meetings without the hassle of back-and-forth emails',
    icon: CalendlyIcon,
    docs: makeDocsLink('calendly'),
    configured: !!process.env.CALENDLY_CLIENT_ID,
    authUrl: () =>
      `https://calendly.com/oauth/authorize?client_id=${
        process.env.CALENDLY_CLIENT_ID
      }&response_type=code&redirect_uri=${makeRedirectUri(
        'calendly',
      )}&state=${generateOAuthStateValue()}`,
  },
  [OrgIntegrationService.hubspot]: {
    isCrm: true,
    name: 'HubSpot',
    description: 'Full platform of marketing, sales, customer service, and CRM software',
    icon: HubspotIcon,
    docs: makeDocsLink('hubspot'),
    configured: !!process.env.HUBSPOT_CLIENT_ID,
    authUrl: () =>
      `https://app.hubspot.com/oauth/authorize?client_id=${
        process.env.HUBSPOT_CLIENT_ID
      }&redirect_uri=${makeRedirectUri(
        'hubspot',
      )}&scope=oauth%20crm.lists.read%20crm.objects.contacts.read%20crm.objects.contacts.write%20crm.objects.companies.write%20crm.schemas.contacts.read%20crm.lists.write%20crm.objects.companies.read%20crm.objects.deals.read%20crm.objects.deals.write%20crm.schemas.companies.read%20crm.schemas.companies.write%20crm.schemas.contacts.write%20crm.schemas.deals.read%20crm.schemas.deals.write%20crm.objects.owners.read`,
  },
  [OrgIntegrationService.hubspotMeetings]: {
    name: 'HubSpot Meetings',
    description: 'Helps sales teams automatically schedule appointments with leads',
    icon: HubspotIcon,
    docs: makeDocsLink('hubspot-meetings'),
    configured: true,
    isIframe: true,
  },
  [OrgIntegrationService.slack]: {
    name: 'Slack',
    description: 'Team communication and collaboration into one place',
    icon: SlackIcon,
    docs: makeDocsLink('slack'),
    configured: !!process.env.SLACK_CLIENT_ID,
    authUrl: () => {
      const scopes = encodeURIComponent(
        [
          'incoming-webhook',
          'chat:write',
          'chat:write.customize',
          'chat:write',
          'channels:read',
          'channels:manage',
          'channels:join',
          'channels:history',
          'groups:read',
          'groups:history',
          'groups:write',
          'links:write',
          'users:read.email',
          'users:read',
        ].join(' '),
      );
      return `https://slack.com/oauth/v2/authorize?client_id=${
        process.env.SLACK_CLIENT_ID
      }&scope=${scopes}&redirect_uri=${makeRedirectUri(
        'slack',
      )}&state=${generateOAuthStateValue()}`;
    },
  },
  [OrgIntegrationService.teams]: {
    name: 'Microsoft Teams',
    description: 'Business communication platform developed by Microsoft',
    icon: TeamsIcon,
    docs: makeDocsLink('microsoft-teams'),
    configured: true,
    authUrl: () => 'sup',
  },
  [OrgIntegrationService.acuity]: {
    name: 'Acuity',
    description: 'Online appointment scheduling software',
    icon: AcuityIcon,
    docs: makeDocsLink('acuity'), // TODO
    configured: !!process.env.ACUITY_CLIENT_ID,
    authUrl: () =>
      `https://acuityscheduling.com/oauth2/authorize?response_type=code&scope=api-v1&client_id=${
        process.env.ACUITY_CLIENT_ID
      }&redirect_uri=${makeRedirectUri('acuity')}&state=${generateOAuthStateValue()}`,
  },
  [OrgIntegrationService.zapier]: {
    name: 'Zapier',
    description: 'Workflow automation for everyone',
    icon: ZapierIcon,
    docs: makeDocsLink('zapier'),
    configured: true,
    isIframe: true,
    isAlwaysEnabled: true,
  },
  [OrgIntegrationService.salesforce]: {
    isCrm: true,
    name: 'Salesforce',
    description: 'CRM to unify marketing, sales, service, commerce, and IT',
    icon: SalesforceIcon,
    docs: makeDocsLink('salesforce'),
    configured: !!process.env.SALESFORCE_CLIENT_ID,
    authUrl: () =>
      `https://login.salesforce.com/services/oauth2/authorize` +
      `?client_id=${process.env.SALESFORCE_CLIENT_ID}` +
      `&redirect_uri=${makeRedirectUri('salesforce')}` +
      `&response_type=code` +
      `&immediate=false` +
      `&display=page` +
      `&state=${generateOAuthStateValue()}`,
  },
  [OrgIntegrationService.salesloft]: {
    isCrm: true,
    name: 'Salesloft',
    description: 'The first AI-powered revenue orchestration platform.',
    icon: SalesloftIcon,
    docs: makeDocsLink('salesloft'),
    configured: !!process.env.SALESLOFT_CLIENT_ID,
    authUrl: () =>
      `https://accounts.salesloft.com/oauth/authorize` +
      `?client_id=${process.env.SALESLOFT_CLIENT_ID}` +
      `&redirect_uri=${makeRedirectUri('salesloft')}` +
      `&response_type=code`,
  },
  [OrgIntegrationService.zendesk]: {
    isCrm: true,
    name: 'Zendesk',
    description:
      'Customer support, sales, and other customer communication tools to improve customer relationships.',
    icon: ZendeskIcon,
    docs: makeDocsLink('zendesk'),
    configured: !!process.env.ZENDESK_CLIENT_ID,
    authUrl: (extraData?: Record<string, string>) =>
      `https://${extraData?.subdomain}.zendesk.com/oauth/authorizations/new` +
      `?client_id=${process.env.ZENDESK_CLIENT_ID}` +
      `&redirect_uri=${makeRedirectUri('zendesk')}` +
      `&response_type=code` +
      `&state=${generateOAuthStateValue(extraData)}` +
      `&scope=read tickets:write webhooks:write users:write`,
  },
  [OrgIntegrationService.chilipiper]: {
    name: 'Chili Piper',
    description: 'Helps sales teams automatically schedule appointments with leads',
    icon: ChiliPiperIcon,
    docs: makeDocsLink('chili-piper'),
    configured: true,
    isIframe: true,
  },
  [OrgIntegrationService.outreach]: {
    isCrm: true,
    name: 'Outreach',
    description:
      'Helps sales teams efficiently create pipeline and predictably close more deals.',
    icon: OutreachIcon,
    docs: makeDocsLink('outreach'),
    configured: !!process.env.OUTREACH_CLIENT_ID,
    extraInstructions: (
      <>
        <a
          href="https://marketplace.outreach.io/apps/servicebell?store=unlisted"
          target="_blank"
          rel="noreferrer"
        >
          Install
        </a>
        &nbsp; the Rep.ai application in Outreach if you haven{"'"}t already. &nbsp;
      </>
    ),
    authUrl: (extraData?: Record<string, string>) => {
      const params = new URLSearchParams({
        client_id: process.env.OUTREACH_CLIENT_ID || '',
        redirect_uri: makeRedirectUri('outreach'),
        response_type: 'code',
        scope: [
          'accounts.all',
          'callDispositions.read',
          'callPurposes.read',
          'calls.all',
          'complianceRequests.read',
          'contentCategories.read',
          'contentCategoryMemberships.read',
          'contentCategoryOwnerships.read',
          'customDuties.write',
          'duties.read',
          'emailAddresses.read',
          'events.read',
          'favorites.read',
          'jobRoles.read',
          'mailAliases.read',
          'mailboxes.read',
          'mailings.read',
          'opportunities.read',
          'opportunityProspectRoles.read',
          'opportunityStages.read',
          'personas.read',
          'phoneNumbers.read',
          'profiles.read',
          'prospects.all',
          'recipients.read',
          'recordActorAssignments.read',
          'roles.read',
          'rulesets.read',
          'sequenceStates.read',
          'sequenceSteps.read',
          'sequenceTemplates.read',
          'sequences.read',
          'snippets.read',
          'stages.read',
          'taskPriorities.read',
          'tasks.all',
          'teams.read',
          'templates.read',
          'users.read',
          'webhooks.all',
        ].join(' '),
        state: generateOAuthStateValue(extraData),
      }).toString();

      return `https://api.outreach.io/oauth/authorize?${params}`;
    },
  },
  [OrgIntegrationService.apollo]: {
    name: 'Apollo',
    icon: ApolloIcon,
    description:
      'Power your go-to-market with one platform. Fueled by the most accurate data on 275M contacts and 73M companies.',
    docs: makeDocsLink('apollo'),
    configured: true,
    isCrm: true,
    isIframe: true,
  },
};

const microsoftAuth = (service: string) => () => {
  const parameters = {
    client_id: process.env.OUTLOOK_CALENDAR_CLIENT_ID || '',
    redirect_uri: makeRedirectUri(service, true),
    response_type: 'code',
    response_mode: 'query',
    scope:
      'https://graph.microsoft.com/Calendars.ReadWrite https://graph.microsoft.com/User.Read https://graph.microsoft.com/OnlineMeetings.ReadWrite offline_access',
  };
  return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${new URLSearchParams(
    parameters,
  ).toString()}`;
};

export const userIntegrationData: Record<UserIntegrationService, IntegrationData> = {
  [UserIntegrationService['google-calendar']]: {
    name: 'Google Calendar',
    description: 'Connect your availability status to your Google Calendar events',
    icon: GoogleCalIcon,
    docs: makeDocsLink('google-calendar'),
    configured: !!process.env.GOOGLE_CLIENT_ID,
    authUrl: () => {
      const parameters = {
        client_id: process.env.GOOGLE_CLIENT_ID || '',
        redirect_uri: makeRedirectUri('google-calendar', true),
        response_type: 'code',
        scope:
          'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events',
        access_type: 'offline',
        prompt: 'consent',
      };
      return `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams(
        parameters,
      ).toString()}`;
    },
    isCalendar: true,
  },
  [UserIntegrationService['outlook-calendar']]: {
    name: 'Outlook Calendar',
    description: 'Connect your availability status to your Outlook Calendar events',
    icon: OutlookIcon,
    docs: makeDocsLink('outlook-calendar'),
    configured: !!process.env.OUTLOOK_CALENDAR_CLIENT_ID,
    authUrl: microsoftAuth('outlook-calendar'),
    isCalendar: true,
  },
  [UserIntegrationService.zoom]: {
    name: 'Zoom',
    description: 'Connect your Zoom account to enable Zoom Meeting calendar events',
    icon: ZoomIcon,
    docs: makeDocsLink('zoom'),
    configured: !!process.env.ZOOM_CLIENT_ID,
    authUrl: () => {
      const parameters = {
        client_id: process.env.ZOOM_CLIENT_ID || '',
        redirect_uri: makeRedirectUri('zoom', true),
        response_type: 'code',
        scope: 'meeting:read meeting:write',
      };
      return `https://zoom.us/oauth/authorize?${new URLSearchParams(
        parameters,
      ).toString()}`;
    },
  },
  [UserIntegrationService['microsoft-teams']]: {
    name: 'Microsoft Teams',
    description:
      'Connect your Microsoft Teams account to enable Teams Meeting calendar events',
    icon: TeamsIcon,
    docs: makeDocsLink('microsoft-teams'),
    configured: !!process.env.OUTLOOK_CALENDAR_CLIENT_ID,
    authUrl: microsoftAuth('microsoft-teams'),
  },
};

export const integrationData = { ...orgIntegrationData, ...userIntegrationData };

const LS_OAUTH_STATE_KEY = '__sb_oauth_state__';

export const generateOAuthStateValue = (extraData?: Record<string, string>) => {
  const nonce = Math.random().toString(36).substring(2);
  localStorage.setItem(LS_OAUTH_STATE_KEY, nonce);
  return JSON.stringify({ nonce, extraData });
};

export const verifyOAuthNonce = (nonce: string) => {
  const storedValue = localStorage.getItem(LS_OAUTH_STATE_KEY);
  if (!nonce && !storedValue) {
    return true;
  }
  localStorage.removeItem(LS_OAUTH_STATE_KEY);
  return nonce === storedValue;
};

export const extractPlanCapAndData = (org: OrgWithPlan, integration: IntegrationData) => {
  const { planCapKey, planDataKey } = integration;
  let cap: number | undefined;
  let data: object | undefined;
  if (planCapKey && org.plan[planCapKey]) {
    const theCap = org.plan[planCapKey];
    if (typeof theCap === 'number') {
      cap = Number(theCap);
    }
  }
  if (planDataKey && org.plan[planDataKey]) {
    data = org.plan[planDataKey] as object;
  }
  return { cap, data };
};

export const isUsingGlobalCredits = (org: OrgWithPlan, integration: IntegrationData) => {
  if (integration.planCapKey && org && org.plan[integration.planCapKey]) {
    const cap = org.plan[integration.planCapKey];
    if (typeof cap === 'number' && cap > 0) {
      return true;
    }
  }
  return false;
};

export const isFullSyncDone = (integration: {
  data: { [key: string]: any };
}): boolean => {
  return (
    integration.data['syncReady'] === true || integration.data['sync2Ready'] === true
  );
};
